import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { setBsLoyaltyEnabled } from '../../../gateways/breadstack/loyaltyApis'
import {
  usePantryRefreshStore,
  usePantryStore,
} from '../../../providers/PantryProvider'

// The active store carries `enableBsLoyalty` (auth maps it from the API's
// snake_case `enable_bs_loyalty`; see mapAuthState). PantryStore keeps this
// behind its index signature, so read it through a narrow local shape.
interface StoreWithLoyalty {
  storeId?: string
  enableBsLoyalty?: boolean
}

interface UseBsLoyaltyToggleResult {
  enabled: boolean
  isSaving: boolean
  toggle: (next: boolean) => void
}

/**
 * Drives the store-level loyalty program on/off flag (`enable_bs_loyalty`) from
 * the Points tab, reusing the Settings → Integrations endpoint
 * (`PUT /stores/bs-loyalty`). The persisted value is read from the pantry store
 * context; an optimistic override keeps the toggle responsive while the write
 * is in flight and until the refreshed store value catches up.
 */
export const useBsLoyaltyToggle = (): UseBsLoyaltyToggleResult => {
  const store = usePantryStore() as StoreWithLoyalty | null
  const refreshStore = usePantryRefreshStore()
  const storeId = store?.storeId ?? ''
  const persisted = Boolean(store?.enableBsLoyalty)

  // `null` = defer to the persisted store value; a boolean = optimistic override
  // held only until the store re-pulls (onSuccess) or the write fails (revert).
  const [optimistic, setOptimistic] = useState<boolean | null>(null)

  const mutation = useMutation({
    mutationFn: (next: boolean) => setBsLoyaltyEnabled(storeId, next),
    // Re-pull auth so the store's enable_bs_loyalty reflects the new value.
    onSuccess: () => refreshStore(),
    // Hand back to the persisted value, reverting the optimistic flip.
    onError: () => setOptimistic(null),
  })

  // Once the refreshed store value matches the optimistic flip, drop the
  // override so the store is the single source of truth again.
  useEffect(() => {
    if (optimistic !== null && persisted === optimistic) {
      setOptimistic(null)
    }
  }, [optimistic, persisted])

  const toggle = (next: boolean) => {
    if (!storeId || mutation.isPending) return
    setOptimistic(next)
    mutation.mutate(next)
  }

  return {
    enabled: optimistic ?? persisted,
    isSaving: mutation.isPending,
    toggle,
  }
}
